import { useQuery } from "@tanstack/react-query";
import {
  Apple,
  Ban,
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
  CloudSun,
  HeartPulse,
  Leaf,
  ShieldAlert,
  Sparkles,
  ThumbsUp,
  User,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { getVanKhiTietKhi, type VanKhiTietKhi } from "@/lib/boide-api";
import { cn } from "@/lib/utils";

import { AlmanacGroup, AlmanacRow } from "./van-khi-row";

type Region = "bac" | "nam";
const REGION_KEY = "boide_tiet_khi_region";

function readRegion(): Region {
  try {
    return window.localStorage.getItem(REGION_KEY) === "nam" ? "nam" : "bac";
  } catch {
    return "bac";
  }
}

/** "2026-10-08T13:30" (giờ VN, không múi) → "13:30 · 8/10". Không qua Date để khỏi lệch múi giờ. */
function moment(iso: string): { time: string; day: string } {
  const [d, t = ""] = iso.split("T");
  const [, m, dd] = d.split("-");
  return { time: t.slice(0, 5), day: `${Number(dd)}/${Number(m)}` };
}

/** yyyy-mm-dd của một mốc ISO cộng thêm `delta` ngày (dùng để nhảy sang tiết trước/sau). */
function shiftDay(iso: string, delta: number): string {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + delta));
  return dt.toISOString().slice(0, 10);
}

/**
 * Thẻ Tiết khí: tiết đang chạy, ngày thứ mấy trong tiết, giờ khởi/chuyển tiết, và
 * hộp chi tiết (thời tiết theo MIỀN, nên/không nên, ăn uống, tạng phủ theo mùa +
 * Tứ quý, lời khuyên riêng theo mệnh). Bấm mũi tên để xem tiết trước/sau.
 */
export function VanKhiTietKhiCard({ className }: { className?: string }) {
  const [date, setDate] = useState<string | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const query = useQuery({
    queryKey: ["van-khi-tiet-khi", date ?? "today"],
    queryFn: () => getVanKhiTietKhi(date),
    staleTime: 30 * 60 * 1000,
  });

  if (query.isLoading) return <Skeleton className={cn("h-56 w-full rounded-3xl", className)} />;
  if (!query.data) return null;

  const tk = query.data.tiet_khi;
  const isToday = query.data.date === query.data.today;
  const start = moment(tk.bat_dau);
  const end = moment(tk.ket_thuc);

  return (
    <section
      aria-label={`Tiết khí ${tk.ten}`}
      className={cn("almanac-panel p-4 sm:p-5", className)}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-semibold text-muted-foreground">
          {isToday ? `Ngày ${tk.ngay_thu}/${tk.tong_ngay}` : "Tiết khác"} · Tháng{" "}
          {tk.thang_tiet.can_chi}
        </p>
        <div className="flex shrink-0 gap-1">
          <button
            type="button"
            aria-label="Tiết trước"
            onClick={() => setDate(shiftDay(tk.bat_dau, -1))}
            className="grid size-7 place-items-center rounded-full border border-border/60 text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="size-4" />
          </button>
          <button
            type="button"
            aria-label="Tiết sau"
            onClick={() => setDate(shiftDay(tk.ket_thuc, 1))}
            className="grid size-7 place-items-center rounded-full border border-border/60 text-muted-foreground hover:text-foreground"
          >
            <ChevronRight className="size-4" />
          </button>
        </div>
      </div>

      <div className="mt-3 flex items-start gap-3">
        <span className="grid size-11 shrink-0 place-items-center rounded-full border border-gold-soft/60 text-primary">
          <CloudSun className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-lg font-bold text-foreground">
            Tiết {tk.ten}
            <span className="ml-2 inline-block whitespace-nowrap rounded-full border border-gold-soft/60 px-2 py-0.5 align-middle text-[10px] font-semibold text-primary">
              mùa {tk.mua}
            </span>
            {tk.tu_quy ? (
              <span className="ml-1.5 inline-block whitespace-nowrap rounded-full border border-amber-500/40 px-2 py-0.5 align-middle text-[10px] font-semibold text-amber-700 dark:text-amber-300">
                Tứ quý
              </span>
            ) : null}
          </h2>
          <p className="mt-0.5 text-sm leading-snug text-foreground/85">{tk.slogan}</p>
        </div>
      </div>

      {tk.ca_nhan ? (
        <p className="mt-3 flex gap-2 rounded-xl bg-primary/[0.06] px-3 py-2 text-[12px] leading-5 text-foreground/85">
          <User className="mt-0.5 size-3.5 shrink-0 text-primary" />
          <span>
            <strong className="font-semibold">
              Mệnh {tk.ca_nhan.menh} · {tk.ca_nhan.term}:
            </strong>{" "}
            {tk.ca_nhan.loi_khuyen}
          </span>
        </p>
      ) : null}

      <DayBar tk={tk} highlightToday={isToday} />

      <div className="mt-3 flex items-end justify-between gap-3 text-[11px] text-muted-foreground">
        <div>
          <p className="font-display text-sm font-bold text-foreground">{tk.ten}</p>
          <p>
            Khởi {start.day} · {start.time}
          </p>
        </div>
        {isToday && tk.con_ngay > 0 ? (
          <p className="pb-0.5 text-center">còn {tk.con_ngay} ngày</p>
        ) : null}
        <div className="text-right">
          <p className="font-display text-sm font-bold text-foreground">{tk.tiet_ke}</p>
          <p>
            Chuyển {end.day} · {end.time}
          </p>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between gap-2">
        {!isToday ? (
          <Button variant="ghost" size="sm" onClick={() => setDate(undefined)}>
            Về tiết hiện tại
          </Button>
        ) : (
          <span />
        )}
        <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
          Đọc kỹ tiết này
          <ChevronsRight className="ml-1 size-4" />
        </Button>
      </div>

      <TietKhiDetail tk={tk} open={open} onOpenChange={setOpen} />
    </section>
  );
}

/**
 * Bản gọn nằm TRONG tờ lịch ngày: tên tiết + mùa + câu tóm, ngày thứ mấy, khi nào
 * chuyển tiết, và nút mở hộp chi tiết (giống hệt thẻ đầy đủ). Dữ liệu lấy từ chính
 * response Lịch ngày (`tiet_khi_info`) nên đổi ngày trên tờ lịch là tiết đổi theo.
 */
export function VanKhiTietKhiInline({ tk }: { tk: VanKhiTietKhi }) {
  const [open, setOpen] = useState(false);
  const start = moment(tk.bat_dau);
  const end = moment(tk.ket_thuc);
  return (
    <AlmanacGroup>
      <AlmanacRow icon={<CloudSun />} label="Tiết khí">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-foreground">
              <strong className="font-semibold">{tk.ten}</strong>
              <span className="text-[12px] text-muted-foreground">
                {" "}
                · mùa {tk.mua}
                {tk.tu_quy ? " · Tứ quý" : ""} · ngày {tk.ngay_thu}/{tk.tong_ngay}
              </span>
            </p>
            <p className="text-[12px] text-foreground/80">{tk.slogan}</p>
            <p className="text-[11px] text-muted-foreground">
              {/* Ngày giao tiết thuộc tiết mới (quy ước lịch in) — nói rõ giờ giao cho khỏi nhầm. */}
              {tk.ngay_thu === 1 ? `Giao tiết lúc ${start.time} ${start.day} · ` : ""}
              Sang {tk.tiet_ke} lúc {end.time} {end.day}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="shrink-0 text-[12px] font-semibold text-primary"
          >
            Đọc kỹ
          </button>
        </div>
      </AlmanacRow>
      <TietKhiDetail tk={tk} open={open} onOpenChange={setOpen} />
    </AlmanacGroup>
  );
}

/**
 * Thanh ngày trong tiết: ngày đã qua tô đậm, hôm nay nổi bật, ngày thuộc 18 ngày
 * Tứ quý gạch chân màu hổ phách. Suy ngày tứ quý từ `ngay_toi_lap` (số ngày từ
 * HÔM ĐÓ tới Lập kế tiếp) để FE không phải tự tính lịch.
 */
function DayBar({ tk, highlightToday }: { tk: VanKhiTietKhi; highlightToday: boolean }) {
  const days = Array.from({ length: tk.tong_ngay }, (_, i) => i + 1);
  return (
    <div className="mt-4">
      <div
        className="flex gap-[3px]"
        role="img"
        aria-label={`Ngày ${tk.ngay_thu} trên ${tk.tong_ngay} ngày của tiết`}
      >
        {days.map((n) => {
          const daysToLap = tk.ngay_toi_lap - (n - tk.ngay_thu);
          const tuQuy = daysToLap > 0 && daysToLap <= 18;
          const today = highlightToday && n === tk.ngay_thu;
          const past = highlightToday && n < tk.ngay_thu;
          return (
            <span
              key={n}
              className={cn(
                "relative grid h-7 min-w-0 flex-1 place-items-center rounded-[5px] text-[9px] font-semibold sm:text-[10px]",
                today
                  ? "bg-primary text-primary-foreground shadow-[0_4px_10px_rgba(130,87,17,0.3)]"
                  : past
                    ? "bg-primary/25 text-foreground/70"
                    : "bg-muted/50 text-muted-foreground",
              )}
            >
              {n}
              {tuQuy ? (
                <span className="absolute inset-x-0.5 -bottom-1 h-[3px] rounded-full bg-amber-500/80" />
              ) : null}
            </span>
          );
        })}
      </div>
      {/* Có ít nhất một ngày tứ quý trong thanh: ngày cuối còn ≤ 18 ngày tới Lập. */}
      {tk.ngay_toi_lap - (tk.tong_ngay - tk.ngay_thu) <= 18 &&
      tk.ngay_toi_lap + (tk.ngay_thu - 1) > 0 ? (
        <p className="mt-2 flex items-center gap-1.5 text-[10px] text-muted-foreground">
          <span className="h-[3px] w-4 rounded-full bg-amber-500/80" /> 18 ngày Tứ quý cuối mùa (Thổ
          vượng)
        </p>
      ) : null}
    </div>
  );
}

function Section({
  icon,
  title,
  items,
  tone = "default",
}: {
  icon: ReactNode;
  title: string;
  items: string[];
  tone?: "good" | "bad" | "default";
}) {
  return (
    <section>
      <h4
        className={cn(
          "flex items-center gap-2 text-sm font-bold",
          tone === "good"
            ? "text-element-wood"
            : tone === "bad"
              ? "text-destructive"
              : "text-foreground",
        )}
      >
        {icon}
        {title}
      </h4>
      <ul className="mt-1.5 space-y-1">
        {items.map((item) => (
          <li key={item} className="flex gap-2 text-[13px] leading-5 text-foreground/85">
            <span className="mt-2 size-1.5 shrink-0 rounded-full bg-primary/60" />
            <span className="min-w-0 flex-1">{item}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function TietKhiDetail({
  tk,
  open,
  onOpenChange,
}: {
  tk: VanKhiTietKhi;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [region, setRegion] = useState<Region>("bac");
  useEffect(() => {
    if (open) setRegion(readRegion());
  }, [open]);
  const pickRegion = (next: Region) => {
    setRegion(next);
    try {
      window.localStorage.setItem(REGION_KEY, next);
    } catch {
      /* chế độ riêng tư: bỏ qua, chỉ mất ghi nhớ */
    }
  };
  const start = moment(tk.bat_dau);
  const end = moment(tk.ket_thuc);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* flex-col + vùng cuộn min-h-0: DialogContent mặc định là grid, thiếu là không cuộn được. */}
      <DialogContent className="flex max-h-[88vh] flex-col overflow-hidden p-0 sm:max-w-lg">
        <DialogHeader className="shrink-0 border-b border-border/60 px-5 pb-3 pt-5 text-left">
          <DialogTitle className="font-display text-xl">Tiết {tk.ten}</DialogTitle>
          <DialogDescription>{tk.slogan}</DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4">
          <section className="space-y-1 text-[13px] leading-5 text-foreground/85">
            <h4 className="flex items-center gap-2 text-sm font-bold text-foreground">
              <CalendarClock className="size-4 text-primary" /> Mốc giờ
            </h4>
            <p>
              Khởi khoảng {start.time} ngày {start.day} → chuyển sang {tk.tiet_ke} khoảng {end.time}{" "}
              ngày {end.day} ({tk.tong_ngay} ngày)
            </p>
            <p className="text-muted-foreground">
              {tk.am_lich} · tháng tiết {tk.thang_tiet.can_chi} (tháng {tk.thang_tiet.so})
            </p>
            <p className="text-muted-foreground">
              Mặt trời ở kinh độ hoàng đạo {tk.kinh_do}°. {tk.dac_diem}
            </p>
          </section>

          <section>
            <div className="flex items-center justify-between gap-2">
              <h4 className="flex items-center gap-2 text-sm font-bold text-foreground">
                <CloudSun className="size-4 text-primary" /> Trời đất hai miền
              </h4>
              <div className="flex rounded-full border border-border/60 p-0.5 text-[11px] font-semibold">
                {(["bac", "nam"] as const).map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => pickRegion(r)}
                    className={cn(
                      "rounded-full px-2.5 py-1 transition",
                      region === r ? "bg-primary text-primary-foreground" : "text-muted-foreground",
                    )}
                  >
                    {r === "bac" ? "Miền Bắc" : "Miền Nam"}
                  </button>
                ))}
              </div>
            </div>
            <p className="mt-1.5 text-[13px] leading-5 text-foreground/85">
              {tk.thoi_tiet[region]}
            </p>
          </section>

          <div className="grid gap-4 sm:grid-cols-2">
            <Section
              icon={<ThumbsUp className="size-4" />}
              title="Hợp làm lúc này"
              items={tk.nen_lam}
              tone="good"
            />
            <Section
              icon={<Ban className="size-4" />}
              title="Nên gác lại"
              items={tk.khong_nen}
              tone="bad"
            />
          </div>

          <section className="rounded-2xl border border-border/60 p-3">
            <h4 className="flex items-center gap-2 text-sm font-bold text-foreground">
              <HeartPulse className="size-4 text-primary" /> Cơ thể trong mùa {tk.mua.toLowerCase()}
            </h4>
            <p className="mt-1.5 text-[13px] leading-5 text-foreground/85">
              Mùa {tk.mua}: hành <strong>{tk.hanh_mua}</strong>, tạng phủ{" "}
              <strong>{tk.tang_phu_mua}</strong>.
            </p>
            {tk.tu_quy && tk.tu_quy_note ? (
              <p className="mt-1 text-[12px] leading-5 text-amber-800 dark:text-amber-300">
                {tk.tu_quy_note}
              </p>
            ) : null}
          </section>

          <Section
            icon={<Leaf className="size-4 text-primary" />}
            title="Giữ sức theo mùa"
            items={tk.cham_soc}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <Section
              icon={<Apple className="size-4" />}
              title="Món hợp tiết"
              items={tk.nen_an}
              tone="good"
            />
            <Section
              icon={<ShieldAlert className="size-4" />}
              title="Ăn ít lại"
              items={tk.han_che}
              tone="bad"
            />
          </div>

          <section className="rounded-2xl bg-primary/[0.06] p-3">
            <h4 className="flex items-center gap-2 text-sm font-bold text-foreground">
              <Sparkles className="size-4 text-primary" /> Lời nhắn của tiết
            </h4>
            <p className="mt-1 text-[13px] leading-5 text-foreground/85">{tk.tinh_than}</p>
            {tk.ca_nhan ? (
              <p className="mt-2 text-[13px] leading-5 text-foreground/85">
                <strong>
                  Với mệnh {tk.ca_nhan.menh} ({tk.ca_nhan.term}):
                </strong>{" "}
                {tk.ca_nhan.loi_khuyen}
              </p>
            ) : null}
          </section>

          <p className="text-[11px] leading-5 text-muted-foreground">
            Lời khuyên là nếp sinh hoạt theo mùa trong dân gian, không thay cho chỉ định của bác sĩ.
            Giờ giao tiết tính theo vị trí mặt trời, có thể lệch vài phút so với lịch in.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
